import { DataGrid, DataGridComponents } from '@neo4j-ndl/react';
import type { CypherDataType, StringStyle } from '@nx/neo4j-sdk';
import { cypherDataToString, isCypherBasicPropertyType } from '@nx/neo4j-sdk';
import { Arrays, isNonEmptyString } from '@nx/stdlib';
import type {
  CellContext,
  ColumnDef,
  FilterFn,
  OnChangeFn,
  Row,
  RowSelectionState,
  Table as TableType,
} from '@tanstack/react-table';
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useVirtualizer } from '@tanstack/react-virtual';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import { type QueryResult, isInt } from 'neo4j-driver';
import type { JSX} from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { ClipboardCopier } from '../clipboard-copier/clipboard-copier';
import type { TableComponentsInterface } from '../ndl-table';
import { CypherDataToJSX, countComplexity } from './record-to-jsx';
import { getHeaderKeys, sortCypherValues } from './utils/data-table';

function shuffleArray(array: number[]) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j] ?? 0;
    array[j] = temp ?? 0;
  }
  return array;
}

type RenderCellOptions = {
  alreadyStringfied?: string;
  stringStyle?: StringStyle;
  jsxRender?: boolean;
  usePrettyPrint: boolean;
  quoteStrings?: boolean;
};

export const renderCell = (
  entry: CypherDataType,
  {
    alreadyStringfied,
    stringStyle = 'json',
    jsxRender = false,
    usePrettyPrint,
    quoteStrings = true,
  }: RenderCellOptions,
) => {
  if (jsxRender) {
    return (
      <span className="text-palette-neutral-text-weak code relative m-0 font-mono text-sm">
        <CypherDataToJSX map={entry} stringStyle={stringStyle} usePrettyPrint={usePrettyPrint} />
      </span>
    );
  }

  if (Array.isArray(entry)) {
    const children = entry.map((item, index) => (
      <React.Fragment key={index}>
        {renderCell(item, { alreadyStringfied: undefined, usePrettyPrint: usePrettyPrint, quoteStrings: quoteStrings })}
        {index === entry.length - 1 ? null : ', '}
      </React.Fragment>
    ));

    return <span className="whitespace-pre-wrap">[{children}]</span>;
  } else if (typeof entry === 'object') {
    if (entry === null) {
      return <em>null</em>;
    }

    if (isInt(entry)) {
      return entry.toString();
    }

    const text = alreadyStringfied ?? cypherDataToString(entry, stringStyle, quoteStrings);

    return <pre className="text-palette-neutral-text-weak relative whitespace-pre-line text-sm">{text}</pre>;
  }

  const text = alreadyStringfied ?? cypherDataToString(entry, stringStyle, quoteStrings);
  return <>{text}</>;
};

type Data = Record<string, CypherDataType>;

const filterCypherValues: FilterFn<Data> = (row, columnId, filterValue) => {
  const cypherData = row.getValue<CypherDataType>(columnId);
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const stringified = cypherDataToString(cypherData, 'cypher');

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return stringified.toLowerCase().includes((filterValue as string).toLowerCase());
};

function CypherCell({
  stringStyle = 'json',
  jsxRender = false,
  usePrettyPrint = true,
  quoteStrings = true,
  ...props
}: CellContext<Data, CypherDataType> & {
  stringStyle: StringStyle;
  jsxRender: boolean;
  usePrettyPrint: boolean;
  quoteStrings?: boolean;
}) {
  const cellContent = useMemo(() => {
    return renderCell(props.getValue(), {
      stringStyle: stringStyle,
      jsxRender: jsxRender,
      usePrettyPrint: usePrettyPrint,
      quoteStrings: quoteStrings,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.getValue(), stringStyle]);
  const dataString = cypherDataToString(props.getValue(), stringStyle);

  return (
    // NDL table has align-items center
    <div className="max-w-full self-start">
      <span>{cellContent}</span>
      <span className="absolute right-1 top-1 opacity-0 group-hover:opacity-100">
        <ClipboardCopier
          textToCopy={dataString}
          title="Copy to clipboard"
          ariaLabel="copy cell content to the clipboard"
          clean={false}
          placement="top"
          floating
        />
      </span>
    </div>
  );
}

type TableComponentProps = {
  queryResult: QueryResult;
  globalFilter?: string | null;
  setGlobalFilter?: (newFilter: string) => void;
  columnSizes?: number[];
  onColumnResize?: (sizes: number[]) => void;
  usePrettyPrint?: boolean;
  resizable?: boolean;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: OnChangeFn<RowSelectionState> | undefined;
};

function getHorizontalSizeForDataType(dataType?: CypherDataType): Partial<{ minSize: number; size: number }> {
  const isBasic = dataType === undefined || isCypherBasicPropertyType(dataType);
  const isShortSimpleArray =
    Array.isArray(dataType) &&
    (dataType[0] === undefined || isCypherBasicPropertyType(dataType)) &&
    dataType.length < 10;

  if (!isBasic && !isShortSimpleArray) {
    return { minSize: 280 };
  }
  return { minSize: 50 };
}

const curriedBodyCell = (
  singleColumn: boolean,
  requiresExtraPadding: boolean,
): TableComponentsInterface<Data>['BodyCell'] =>
  function BodyCellWrapper({ innerProps, ...props }): JSX.Element {
    return (
      <DataGridComponents.BodyCell
        {...props}
        innerProps={{
          ...innerProps,
          className: cx(
            innerProps?.className,
            'group break-all !pl-3 !pr-0',
            requiresExtraPadding ? 'first:!pl-6' : 'first:!pl-4',
            singleColumn ? 'min-w-fit !whitespace-pre' : '!whitespace-pre-line',
          ),
        }}
      />
    );
  };

const getShiftSelection = (filteredRows: Row<Data>[], from: number, to: number): Record<string, boolean> => {
  const selection: Record<string, boolean> = {};
  for (let i = from; i <= to; i++) {
    const id = filteredRows[i]?.id;
    if (id !== undefined) {
      selection[id] = true;
    }
  }
  return selection;
};

const BodyCurryingFunction = (
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  ref: React.MutableRefObject<HTMLDivElement | null>,
  table: TableType<Data>,
): TableComponentsInterface<Data>['Body'] =>
  function BodyWrapper({ innerProps, children, ...props }) {
    const [lastSelectedRow, setLastSelectedRow] = React.useState<number | null>(null);
    const { rows } = table.getSortedRowModel();
    rowVirtualizer.setOptions({
      ...rowVirtualizer.options,
      count: rows.length,
    });
    let items = rowVirtualizer.getVirtualItems();
    // We were running into issues with the app crashing when the rows unmount (and get size 0).
    // This was causing the virtualizer to think it should render more items (since now they all fit in the viewport)
    // That led to rerendering until all rows are in the virtualizer,
    // which crashes the app if there are > 100 rows or so.
    // There is probably a better way to do this.
    if (ref.current?.getBoundingClientRect().height === 0) {
      items = [];
    }

    const handleOnRowClick = (e: React.MouseEvent, foundRow: Row<Data>) => {
      if (e.shiftKey && lastSelectedRow !== null) {
        const { rows: filteredRows } = table.getRowModel();
        const startIdx = filteredRows.findIndex((r) => r.index === lastSelectedRow);
        const endIdx = filteredRows.findIndex((r) => r.index === foundRow.index);
        const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        table.setRowSelection(getShiftSelection(filteredRows, from, to));
        setLastSelectedRow(foundRow.index);
        return;
      }

      if (e.shiftKey) {
        foundRow.toggleSelected();
        setLastSelectedRow(foundRow.index);
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        foundRow.toggleSelected();
        setLastSelectedRow(foundRow.index);
        return;
      }

      table.setRowSelection({ [foundRow.id]: !foundRow.getIsSelected() });
      setLastSelectedRow(foundRow.index);
    };

    return (
      <div>
        <DataGridComponents.Body
          {...props}
          innerProps={{
            ...innerProps,
            className: innerProps?.className ?? '',
            style: { position: 'relative', height: `${rowVirtualizer.getTotalSize()}px`, width: '100%' },
          }}
        >
          {items.length === 0 && <div className="mt-10 flex w-full justify-center font-bold"> No data present</div>}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${items[0]?.start ?? 0}px)`,
            }}
          >
            {items.map((virtualItem) => {
              const foundRow = rows[virtualItem.index];
              if (!foundRow) {
                return null;
              }

              return (
                <div
                  key={virtualItem.key}
                  ref={rowVirtualizer.measureElement}
                  data-index={virtualItem.index}
                  className="relative"
                >
                  <span className="text-palette-neutral-text-weakest z-overlay absolute left-1 top-3 text-[8px]">
                    {virtualItem.index + 1}
                  </span>
                  <DataGridComponents.BodyRow
                    row={foundRow}
                    innerProps={{
                      className: foundRow.getIsSelected()
                        ? '!bg-palette-primary-bg-weak [&>*]:!bg-palette-primary-bg-weak'
                        : '',
                      onClick: (e) => handleOnRowClick(e, foundRow),
                      htmlAttributes: {
                        onMouseDown: (e) => {
                          if (e.shiftKey) {
                            e.preventDefault();
                          }
                        },
                      },
                    }}
                  />
                  {/* Adds back the border on the bottom which gets removed if we wrap the body like this */}
                  <div
                    className="w-(100%-8px)] border-b-palette-neutral-border-weak
                        absolute bottom-0 left-1 right-1 border-b"
                  />
                </div>
              );
            })}
          </div>
        </DataGridComponents.Body>
      </div>
    );
  };

const ScrollableCurryingFunction = (ref: React.MutableRefObject<HTMLDivElement | null>) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, prefer-arrow-callback
  return React.forwardRef(function ScrollableWrapper({ children }, _ref) {
    return (
      <div ref={ref} className="ndl-data-grid-scrollable ">
        {children}
      </div>
    );
  }) as (typeof DataGridComponents)['Scrollable'];
};

function createDebouncedColUpdater(columnIds: string[], resizeDispatchFunction: (sizes: number[]) => void) {
  let lastWidths: Record<string, number> = {};

  function columnUpdate(newWidths: Record<string, number>) {
    const same = columnIds.every((id) => newWidths[id] === lastWidths[id]);
    const isInitialRender = isEmpty(lastWidths);

    if (!same && !isInitialRender) {
      // save ordered list of column widths
      resizeDispatchFunction(columnIds.map((id) => newWidths[id] ?? 0));
    }

    lastWidths = newWidths;
  }
  return debounce(columnUpdate, 500);
}

const COMPLEXITY_THRESHOLD = 1000;

function Neo4jDataTable({
  queryResult,
  globalFilter,
  setGlobalFilter,
  columnSizes,
  onColumnResize = () => {
    /* no op*/
  },
  rowSelection = {},
  setRowSelection = () => {
    /* no op */
  },
  usePrettyPrint = false,
  resizable = false,
}: TableComponentProps): JSX.Element | null {
  let jsxRender = true;
  const spreadableSizes =
    columnSizes !== undefined
      ? Object.entries(columnSizes).reduce<Record<string, { size: number }>>((acc, [key, value]) => {
          acc[key] = { size: value };
          return acc;
        }, {})
      : {};

  const columns = useMemo<ColumnDef<Data, CypherDataType>[]>(
    () =>
      getHeaderKeys(queryResult.records).map((key, idx) => ({
        id: key,
        accessorFn: (row: Data) => row[key],
        cell: (p) => <CypherCell jsxRender={jsxRender} stringStyle="cypher" usePrettyPrint={usePrettyPrint} {...p} />,
        header: () => <span title={key}>{key}</span>,
        sortingFn: sortCypherValues,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        ...getHorizontalSizeForDataType(queryResult.records[0]?.toObject()?.[key] as unknown as CypherDataType),
        ...spreadableSizes[idx],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult, usePrettyPrint],
  );

  const data = useMemo(() => queryResult.records.map((r) => r.toObject()), [queryResult]);
  const scrollingParentRef = useRef<HTMLDivElement | null>(null);

  // Overscan is _very_ expensive, so if we have even relatively complex data we should bring it down.
  // This looks at the complexity of the data and if it is too complex it will reduce the overscan.
  // The memo is a shallow check so it should not be too expensive.
  const sampledAverageComplexity = useMemo(() => {
    const sampleSize = Math.min(1000, data.length);
    const shuffledIndices = shuffleArray(Arrays.range(0, data.length - 1));
    const randomIndices = shuffledIndices.slice(0, sampleSize);
    let total = 0;

    for (const index of randomIndices) {
      const curr = data[index];
      if (curr !== undefined) {
        total += countComplexity(curr);
      }
    }

    return total / sampleSize;
  }, [data]);

  if (sampledAverageComplexity > COMPLEXITY_THRESHOLD) {
    jsxRender = false;
  }

  // This seems like a good way to calculate a reasonable overscan but there could be counterexamples.
  const overscan = Math.max(0, 9 - Math.ceil(sampledAverageComplexity / 4));
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollingParentRef.current,
    // 40 is the size for a basic data type row so fallback to that
    estimateSize: () => 40,
    overscan,
  });

  // When resizing columns, the table fires hundreds of events per second, so committing all of these to the store
  // is not reasonable. Instead, we debounce this and only commit after it has been stable for 500ms.
  // This also means we need to store the column sizes in local state however, since otherwise it will not be
  // responsive to being resized.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedColumnResize = useCallback(
    createDebouncedColUpdater(
      columns.map((column) => column.id!),
      onColumnResize,
    ),
    [],
  );

  const table = useReactTable<Data>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: true,
    getSortedRowModel: getSortedRowModel(),
    state: { rowSelection, globalFilter },
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: filterCypherValues,
    getColumnCanGlobalFilter: () => true,
    columnResizeMode: 'onChange',
  });

  useEffect(() => {
    if (isNonEmptyString(globalFilter)) {
      const newSelectionItems: Record<string, boolean> = {};

      table.getRowModel().rows.forEach((row) => {
        newSelectionItems[row.id] = true;
      });

      setRowSelection(newSelectionItems);
    }
  }, [globalFilter, table, setRowSelection]);

  useEffect(() => {
    const sizes = table.getAllColumns().reduce<Record<string, number>>((acc, c) => {
      acc[c.id] = c.getSize();
      return acc;
    }, {});
    debouncedColumnResize(sizes);
  });

  const CustomBody = useMemo(() => {
    return BodyCurryingFunction(rowVirtualizer, scrollingParentRef, table);
  }, [rowVirtualizer, scrollingParentRef, table]);

  const CustomBodyCell = useMemo(() => {
    return curriedBodyCell(columns.length === 1, data.length >= 1000);
  }, [columns, data.length]);

  const CustomScrollable = useMemo(() => {
    return ScrollableCurryingFunction(scrollingParentRef);
  }, [scrollingParentRef]);

  return (
    <DataGrid
      components={{
        Body: CustomBody,
        BodyCell: CustomBodyCell,
        Scrollable: CustomScrollable,
        Navigation: null,
        RowsPerPage: null,
        PaginationNumericButton: null,
      }}
      isAutoResizingColumns={false}
      isResizable
      tableInstance={table}
      rootProps={{
        className: `${resizable ? 'overflow-auto h-full' : 'overflow-scroll'}
        [&_.ndl-header-cell]:!pl-3
        [&_.ndl-header-cell]:!py-1
        [&_.ndl-data-grid-thead_.ndl-data-grid-tr]:!h-10`,
      }}
      styling={{ headerStyle: 'clean' }}
      isKeyboardNavigable={false}
    />
  );
}

// Add CypherCell as a static property
Neo4jDataTable.CypherCell = CypherCell;

// Export only Neo4jDataTable
export { Neo4jDataTable };
